import { Analytics } from '@cobliteam/analytics';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import type { Column } from '@tanstack/react-table';
import { useEffect, useRef } from 'react';

interface DataTableFilterColumnsProps<T> {
  columns: Column<T>[];
  visibleColumns: Column<T>[];
}

const DataTableFilterColumns = <T,>({ visibleColumns, columns }: DataTableFilterColumnsProps<T>) => {
  const isFirstMount = useRef(true);
  const { t } = useTranslation('ui-business');
  const allVisibleColumns = visibleColumns.map((column) => column.columnDef?.meta?.name);
  const canHideColumns = columns.filter((column) => column.getCanHide());
  const totalColumns = columns.length;
  const showColumns =
    allVisibleColumns.length !== totalColumns ? t('data_table.columns_with_count', { visible: allVisibleColumns.length, total: totalColumns }) : t('data_table.columns');

  const onChangeColumnVisibility = (column: Column<T>) => {
    column.toggleVisibility(!column.getIsVisible());
  };

  useEffect(() => {
    const currentVisibleColumns = visibleColumns.map((column) => column.columnDef?.meta?.name);

    if (isFirstMount.current) {
      isFirstMount.current = false;
    } else {
      Analytics.track('DataTable Columns Changed', {
        selectedColumns: currentVisibleColumns,
      });
    }
  }, [JSON.stringify(visibleColumns)]);

  if (columns.length <= 2 || !canHideColumns.length) return null;

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <Button startIcon={<Icon name='columns-3' />} size='sm' variant='outlined' color='default'>
          {showColumns}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align='start' className='max-h-96 overflow-y-auto'>
        <DropdownMenu.Header>{t('data_table.filter_columns')}</DropdownMenu.Header>
        <DropdownMenu.Separator />
        {canHideColumns.map((column) => {
          return (
            <DropdownMenu.CheckboxItem key={column.id} checked={!!column.getIsVisible()} onCheckedChange={() => onChangeColumnVisibility(column)}>
              <Typography color='secondary'>{column.columnDef.meta?.name}</Typography>
            </DropdownMenu.CheckboxItem>
          );
        })}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default DataTableFilterColumns;
